import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { LogOut, Loader2, FileText, Lock, Trash2, Mail, BadgeCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "daab_admin_token";

const TABS = [
  { key: "applications", label: "Applications", countKey: "applications" },
  { key: "employer-requests", label: "Employer Requests", countKey: "employer_requests" },
  { key: "candidate-submissions", label: "Candidates", countKey: "candidate_submissions" },
  { key: "partner-requests", label: "Partners", countKey: "partner_requests" },
  { key: "verify-requests", label: "Verifications", countKey: "verify_requests" },
  { key: "posted-jobs", label: "Posted Jobs", countKey: "posted_jobs" },
  { key: "contacts", label: "Contacts", countKey: "contacts" },
];

const HIDE = ["attachments", "job_id"];

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  const input = "w-full bg-transparent border-b border-[#e4e4e7] py-3 font-body text-base text-[#0a0a0a] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#ff3b30] transition-colors";
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-[#e4e4e7] p-8 md:p-10">
        <div className="flex items-center gap-2 mb-2"><Lock size={16} className="text-[#ff3b30]" /><span className="font-body text-xs tracking-[0.25em] uppercase text-[#ff3b30]">Admin</span></div>
        <h1 className="font-head uppercase tracking-tight text-4xl mb-8">Sign in</h1>
        <div className="space-y-6">
          <input data-testid="admin-email" className={input} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" className={input} placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button data-testid="admin-login-btn" disabled={loading} className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] disabled:opacity-60 text-white font-head uppercase tracking-wide px-6 py-4 transition-colors">
          {loading ? <>Signing in <Loader2 size={16} className="animate-spin" /></> : "Sign in"}
        </button>
        <Link to="/" className="block mt-6 text-center font-body text-sm text-[#52525b] hover:text-[#0a0a0a]">← Back to site</Link>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [summary, setSummary] = useState({});
  const [tab, setTab] = useState("applications");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem(TOKEN_KEY);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const inputCls = "w-full bg-transparent border-b border-[#e4e4e7] py-3 font-body text-base text-[#0a0a0a] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#ff3b30]";
  const emailOf = (it) => it.email || it.your_email || it.contact_email || "";

  const loadSummary = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/summary`, authHeader); setSummary(data); }
    catch (e) { if (e.response?.status === 401) onLogout(); }
  }, []);

  const loadTab = useCallback(async (kind) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data/${kind}`, authHeader);
      setItems(data.items); setMeta({ url: data.public_url, token: data.download_token });
    } catch (e) { if (e.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const fileUrl = (path) => `${meta.url}/api/files/${path}?t=${meta.token}`;

  const refresh = () => { loadTab(tab); loadSummary(); };
  const del = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try { await axios.delete(`${API}/admin/data/${tab}/${id}`, authHeader); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const publish = async (id) => {
    try { const { data } = await axios.post(`${API}/admin/employer-requests/${id}/publish`, {}, authHeader); toast.success(data.message); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const sendReply = async () => {
    if (!reply.to || !reply.message) { toast.error("Recipient and message required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reply`, { to: reply.to, subject: reply.subject, message: reply.message }, authHeader);
      (data.status === "success" ? toast.success : toast)(data.message);
      setReply(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Reply failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" className="shrink-0">
            <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_daab-recruit/artifacts/kveh0gcx_file_0000000029048211809fc80c6002540d.webp" alt="DAAB Consulting Recruitment Services" className="h-9 md:h-11 w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 font-body text-sm text-[#52525b] hover:text-[#ff3b30]"><LogOut size={16} /> Log out</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`summary-${t.key}`} onClick={() => setTab(t.key)} className={`text-left border p-5 transition-colors ${tab === t.key ? "border-[#ff3b30]" : "border-[#e4e4e7] hover:border-[#0a0a0a]"}`}>
              <div className="font-head text-4xl tracking-tighter">{summary[t.countKey] ?? "—"}</div>
              <div className="font-body text-xs uppercase tracking-wide text-[#52525b] mt-2">{t.label}</div>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#e4e4e7]">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} className={`font-head uppercase tracking-wide text-sm px-4 py-3 -mb-px border-b-2 transition-colors ${tab === t.key ? "border-[#ff3b30] text-[#0a0a0a]" : "border-transparent text-[#a1a1aa] hover:text-[#0a0a0a]"}`}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 size={26} className="animate-spin text-[#ff3b30]" /></div>
        ) : items.length === 0 ? (
          <p className="font-body text-[#52525b] py-16 text-center">No entries yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-items">
            {items.map((it, i) => (
              <div key={i} className="border border-[#e4e4e7] p-6" data-testid={`admin-item-${i}`}>
                <div className="grid grid-cols-1 gap-1.5">
                  {Object.entries(it).filter(([k]) => !HIDE.includes(k)).map(([k, v]) => (
                    <div key={k} className="flex gap-3 font-body text-sm">
                      <span className="text-[#a1a1aa] uppercase tracking-wide text-xs w-40 shrink-0 pt-0.5">{k.replace(/_/g, " ")}</span>
                      <span className="text-[#0a0a0a] break-words">{String(v || "—")}</span>
                    </div>
                  ))}
                </div>
                {Array.isArray(it.attachments) && it.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#e4e4e7]">
                    <span className="text-[#a1a1aa] uppercase tracking-wide text-xs">Attachments</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {it.attachments.map((a, x) => (
                        <a key={x} href={fileUrl(a.path)} target="_blank" rel="noopener noreferrer" data-testid={`file-${i}-${x}`} className="inline-flex items-center gap-2 border border-[#e4e4e7] hover:border-[#ff3b30] px-3 py-2 font-body text-xs">
                          <FileText size={13} className="text-[#ff3b30]" /> {a.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-[#e4e4e7] flex flex-wrap items-center gap-2">
                  <button data-testid={`reply-${i}`} onClick={() => setReply({ to: emailOf(it), subject: "Re: your enquiry — Daab Recruitment", message: "" })} className="inline-flex items-center gap-1.5 border border-[#e4e4e7] hover:border-[#ff3b30] px-3 py-2 font-body text-xs"><Mail size={13} className="text-[#ff3b30]" /> Reply</button>
                  {tab === "employer-requests" && (
                    <button data-testid={`publish-${i}`} onClick={() => publish(it.id)} className="inline-flex items-center gap-1.5 bg-[#0a0a0a] hover:bg-[#ff3b30] text-white px-3 py-2 font-body text-xs uppercase tracking-wide"><BadgeCheck size={13} /> Verify & Publish</button>
                  )}
                  <button data-testid={`delete-${i}`} onClick={() => del(it.id)} className="inline-flex items-center gap-1.5 border border-[#e4e4e7] text-[#ef4444] hover:border-[#ef4444] px-3 py-2 font-body text-xs ml-auto"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {reply && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="reply-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReply(null)} />
          <div className="relative bg-white border border-[#e4e4e7] w-full max-w-lg p-8">
            <button onClick={() => setReply(null)} className="absolute top-4 right-4 text-[#a1a1aa] hover:text-[#0a0a0a]"><X size={20} /></button>
            <h3 className="font-head uppercase text-2xl mb-6">Reply by email</h3>
            <div className="space-y-4">
              <input data-testid="reply-to" className={inputCls} placeholder="To (email)" value={reply.to} onChange={(e) => setReply({ ...reply, to: e.target.value })} />
              <input data-testid="reply-subject" className={inputCls} placeholder="Subject" value={reply.subject} onChange={(e) => setReply({ ...reply, subject: e.target.value })} />
              <textarea data-testid="reply-message" rows={5} className={`${inputCls} resize-none`} placeholder="Your message" value={reply.message} onChange={(e) => setReply({ ...reply, message: e.target.value })} />
              <button data-testid="reply-send" onClick={sendReply} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] disabled:opacity-60 text-white font-head uppercase text-sm px-6 py-3">{busy ? <Loader2 size={16} className="animate-spin" /> : "Send reply"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  const check = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setAuthed(true);
    } catch { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  return (
    <>
      <Toaster position="bottom-right" theme="light" toastOptions={{ style: { background: "#f4f4f5", border: "1px solid #e4e4e7", color: "#0a0a0a", borderRadius: "0" } }} />
      {authed === null ? (
        <div className="min-h-screen flex items-center justify-center"><Loader2 size={26} className="animate-spin text-[#ff3b30]" /></div>
      ) : authed ? (
        <Dashboard onLogout={logout} />
      ) : (
        <Login onDone={() => setAuthed(true)} />
      )}
    </>
  );
}
